import {
    AfterViewInit,
    Component,
    ElementRef,
    Inject,
    Input,
    OnChanges,
    OnInit,
    PLATFORM_ID,
    ViewChild,
} from "@angular/core";
import PhotoSwipeLightbox from "photoswipe/lightbox";

import { AssetFragment } from "../../../common/generated-types";
import { isSupplierNoImagePlaceholder } from "../../../shared/utils/product-image.utils";

import "./types.d";
import { isPlatformBrowser } from "@angular/common";

export type AssetWithDimensions = Pick<
    AssetFragment,
    "id" | "preview" | "width" | "height"
>;

@Component({
    selector: "vsf-asset-gallery",
    templateUrl: "./asset-gallery.component.html",
    styleUrls: ["./asset-gallery.component.scss"],
})
export class AssetGalleryComponent implements OnInit, OnChanges, AfterViewInit {
    @Input() assets?: AssetWithDimensions[] = [];
    @Input() featuredAsset?: AssetWithDimensions;
    @Input() showSingleThumbnail = false;
    @Input() selectedAssetId: string;
    @ViewChild("mainPreview", { static: false })
    featuredAssetLoaded = false;
    private mainPreview: ElementRef<HTMLImageElement>;

    selectedAsset?: AssetWithDimensions;
    private gallery: any;
    imageLoadError = false;

    constructor(@Inject(PLATFORM_ID) private platformId: any) {}

    ngOnInit() {
        this.selectImage(this.selectedAssetId);
    }

    ngOnChanges() {
        if (this.displayAssets.length) {
            this.initPhotoswipe();
            this.selectImage(this.selectedAssetId);
        }
    }

    ngAfterViewInit() {
        if (this.displayAssets.length) {
            this.initPhotoswipe();
        }
    }

    get displayAssets(): AssetWithDimensions[] {
        const assets = [...(this.assets ?? [])];
        if (
            this.featuredAsset &&
            !assets.some((asset) => asset.id === this.featuredAsset!.id)
        ) {
            assets.unshift(this.featuredAsset);
        }
        return assets.filter(
            (asset) =>
                !isSupplierNoImagePlaceholder(asset.preview, asset.id),
        );
    }

    onLoad() {
        this.featuredAssetLoaded = true;
        this.imageLoadError = false;
    }

    onError() {
        this.imageLoadError = true;
        this.featuredAssetLoaded = false;
    }

    private initPhotoswipe() {
        if (isPlatformBrowser(this.platformId)) {
            const items = this.displayAssets.map((asset) => ({
                src: asset.preview,
                msrc: asset.preview + "?preset=medium",
                width: asset.width || 1000,
                height: asset.height || 1000,
            }));
            this.gallery = new PhotoSwipeLightbox({
                dataSource: items,
                pswpModule: () => import("photoswipe"),
                showHideOpacity: true,
            });
            this.gallery.init();
        }
    }

    selectImage(assetId: string) {
        if (assetId != null) {
            this.selectedAsset = this.displayAssets.find((a) => a.id === assetId);
        } else {
            this.selectedAsset = this.displayAssets[0];
        }
        this.imageLoadError = !this.selectedAsset;
        this.featuredAssetLoaded = false;
    }

    openImage(assetId: string) {
        const index = this.displayAssets.findIndex((a) => a.id === assetId);
        if (index < 0) {
            return;
        }
        this.gallery.loadAndOpen(index);
    }
}
